import { CustomerAddressInput } from '@segway/core/src/graphql/generated/graphql'

export type AddressFieldComparison = {
  field: string
  original: string | null
  improved: string | null
  isDifferent: boolean
}

export const extractAddressFields = (
  address: CustomerAddressInput | null,
): Record<string, string> => {
  if (!address) return {}

  return {
    basicInfo: `${address.firstname || ''} ${address.telephone || ''} ${address.lastname || ''}`,
    street: address.street?.filter(Boolean).join(', ') || '',
    cityRegionCountryPostcode: `${address.city || ''} ${address.region?.region || ''} ${address.country_id || ''} ${address.postcode || ''}`,
  }
}

export const compareAddresses = (
  originalAddress: CustomerAddressInput | null,
  improvedAddress: CustomerAddressInput | null,
): AddressFieldComparison[] => {
  const originalFields = extractAddressFields(originalAddress)
  const improvedFields = extractAddressFields(improvedAddress)

  const fieldOrder = ['basicInfo', 'street', 'cityRegionCountryPostcode']

  return fieldOrder.map((field) => ({
    field,
    original: originalFields[field] || null,
    improved: improvedFields[field] || null,
    isDifferent:
      originalFields[field] !== improvedFields[field] &&
      improvedFields[field] !== '' &&
      originalFields[field] !== '',
  }))
}

export const formatAddressField = (
  field: AddressFieldComparison,
  addressType: 'original' | 'improved',
): JSX.Element | null => {
  const value = addressType === 'original' ? field.original : field.improved

  if (!value) return null

  const shouldHighlight = addressType === 'improved' && field.isDifferent

  return (
    <div key={`${addressType}-${field.field}`} className="mt-1">
      <span className={shouldHighlight ? 'bg-[#93ee9e] font-medium' : ''}>{value}</span>
    </div>
  )
}

export const renderFormattedAddress = (
  address: CustomerAddressInput | null,
  comparedFields: AddressFieldComparison[],
  addressType: 'original' | 'improved',
): JSX.Element => {
  if (!address) {
    return <div className="text-gray-500">No address available</div>
  }

  return (
    <div className="mt-2 text-sm">
      {comparedFields.map((field) => formatAddressField(field, addressType))}
    </div>
  )
}
